import { ExternalBatteryPackMode, Solar4gCamera, Solar4gSysInfo } from '@EcamModel/model';
import IcEdit from '@assets/details-camera-icons/IcEdit';
import { pushError } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import { solar4gCameraHttpController } from '@controllers/index';
import usePopUp from '@hooks/usePopUp';
import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import useMileSightSolar from '@pages/cameras/hook/useMilesightSolar';
import { externalPowerBattery } from '@pages/cameras/list';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { CheckData } from './CaptureManagementMilesightCameraSolar';
import PopUpExternalBatteryPackSettings from './PopUpExternalBatteryPackSettings';
import InformationIcon from './icon/InformationIcon';

export default function StatusManagementMilesightCameraSolar() {
    const queryClient = useQueryClient();
    const { id } = useParams<{ id: string }>();

    const backdrop = useBackdrop();

    const { Solar4gSysInfo, solar4gCamera } = useMileSightSolar();
    const popUpExternalBatteryPack = usePopUp();

    const [settingsExpected, setSettingsExpected] = useState<Solar4gSysInfo>(
        solar4gCamera?.Solar4gSysInfoExpected ?? ({} as Solar4gSysInfo)
    );

    const [settings, setSettings] = useState<Solar4gSysInfo>(solar4gCamera?.Solar4gSysInfo ?? ({} as Solar4gSysInfo));

    const updateSysInfo = async (devMac: string, data: Partial<Solar4gSysInfo>) => {
        return await solar4gCameraHttpController.updateSysInfo(devMac, data);
    };

    const updateSysInfoMutation = useMutation(
        ({ devMac, data }: { devMac: string; data: Partial<Solar4gSysInfo> }) => updateSysInfo(devMac, data),
        {
            onMutate: () => {
                backdrop.setTrue();
            },
            onSuccess: (res) => {
                queryClient.setQueryData<Solar4gCamera | undefined>(['solar4gCamera', id], (oldData) => {
                    if (!oldData) return oldData;

                    return {
                        ...oldData,
                        Solar4gSysInfoExpected: res,
                    };
                });
                setSettingsExpected(res);
            },
            onError: (err: AxiosError) => {
                pushError(err.message);
            },
            onSettled: () => {
                backdrop.setFalse();
            },
        }
    );

    useEffect(() => {
        if (solar4gCamera?.Solar4gSysInfoExpected) {
            setSettingsExpected(solar4gCamera?.Solar4gSysInfoExpected);
        }
        if (solar4gCamera?.Solar4gSysInfo) {
            setSettings(solar4gCamera?.Solar4gSysInfo);
        }
    }, [solar4gCamera?.Solar4gCellularInfoExpected, solar4gCamera?.Solar4gSysInfo]);

    return (
        <Stack direction={'column'} spacing={2} my={2}>
            <Box id="external-battery-pack" border={'1px solid #E4E7EC'} borderRadius={2}>
                <Stack paddingX={2} paddingY={1} direction="row" justifyContent={'space-between'}>
                    <Typography fontSize={16}>Status</Typography>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                        <Tooltip title="Edit image adjustment" placement="top" arrow>
                            <IconButton
                                sx={{
                                    borderRadius: '3px',
                                    width: '24px',
                                    height: '24px',
                                    color: 'white',
                                    backgroundColor: '#2E7D32',
                                    ':hover': {
                                        backgroundColor: '#256428',
                                    },
                                    padding: '4px',
                                    '&.MuiIconButton-root.Mui-disabled': {
                                        backgroundColor: '#eee',
                                    },
                                }}
                                onClick={() => {
                                    popUpExternalBatteryPack.setTrue();
                                }}
                            >
                                <IcEdit style={{ width: '14px', height: '14px' }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Divider
                    orientation="vertical"
                    sx={{
                        borderBottomWidth: 1,
                        borderColor: color.grey300,
                        mx: 2,
                    }}
                />

                <Stack paddingY={1} paddingX={2} spacing={2}>
                    {[
                        {
                            label: 'External battery pack',
                            key: 'External battery pack',
                            value: externalPowerBattery?.[
                                Solar4gSysInfo?.ExternalBatteryPack as ExternalBatteryPackMode
                            ]?.Name,
                            edited: Solar4gSysInfo?.ExternalBatteryPack !== settingsExpected?.ExternalBatteryPack,
                            valueEdited:
                                externalPowerBattery?.[settingsExpected?.ExternalBatteryPack as ExternalBatteryPackMode]
                                    ?.Name,
                        },
                    ].map((item) => {
                        return (
                            <CheckData
                                title={
                                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                        <Typography>{item.label}</Typography>
                                        <Tooltip
                                            placement="top"
                                            arrow
                                            title={
                                                <Typography
                                                    sx={{
                                                        whiteSpace: 'pre-line',
                                                        mb: 0,
                                                        color: color.white,
                                                    }}
                                                >
                                                    {Solar4gSysInfo?.ExternalBatteryPack ===
                                                    ExternalBatteryPackMode.ExternalPowerBattery
                                                        ? tooltipTextExternalPower
                                                        : tooltipTextInternalBattery}
                                                </Typography>
                                            }
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        maxWidth: 400,
                                                        whiteSpace: 'normal',
                                                    },
                                                },
                                            }}
                                        >
                                            <InformationIcon />
                                        </Tooltip>
                                    </Stack>
                                }
                                value={<Typography>{item.value}</Typography>}
                                edited={item.edited}
                                valueEdited={<Typography color={color.grey600}>{item.valueEdited}</Typography>}
                                onCancel={async () =>
                                    (await updateSysInfoMutation.mutateAsync({
                                        devMac: solar4gCamera?.Solar4gSysInfo?.DevMac!,
                                        data: {
                                            ExternalBatteryPack:
                                                Solar4gSysInfo?.ExternalBatteryPack as ExternalBatteryPackMode,
                                        },
                                    })) as any
                                }
                            />
                        );
                    })}
                </Stack>
            </Box>

            <PopUpExternalBatteryPackSettings
                {...popUpExternalBatteryPack}
                onSave={async (mac, data) => {
                    await updateSysInfoMutation.mutateAsync({
                        devMac: mac,
                        data,
                    });
                }}
                cellularSettings={settings}
            />
        </Stack>
    );
}

const tooltipTextExternalPower = `When the temperature is below 5°C and the internal battery level is below 80%, the heating function will turn on to warm the internal battery—even if it is not charging. This protects the battery and uses external power, so it does not consume the internal battery.`;

const tooltipTextInternalBattery = `Heating will only turn on when the internal battery is charging and the temperature is below 5°C. If the internal battery is not charging, heating will not activate even in cold weather.`;
